import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { updatePriceRequestDraftTemplate } from 'apollo/templates/price-request';
import { PriceRequest, PriceRequestProduct } from 'models/price-request';
import { formatCurrency } from 'utils/format';
import { errorMessageKey } from './price-request-format';
import { alwaysVisibleScrollbar } from '../always-visible-scrollbar';
import PriceRequestCategoryRanks, { RankedCategory } from './price-request-category-ranks.component';

const MAX_NOTE_LENGTH = 2000;

const selectedOf = (request: PriceRequest) => new Set(request.lines.map((line) => line.action));

const ranksOf = (request: PriceRequest): Record<string, string> =>
    Object.fromEntries(request.categoryRanks.map(({ category, rank }) => [category, String(rank)]));

/** Categories of the ticked products the lab has no price list for: the backend says which. */
const unlistedCategoriesOf = (products: PriceRequestProduct[], selected: Set<string>): RankedCategory[] => {
    const categories = new Map<string, RankedCategory>();
    products
        .filter((product) => selected.has(product.action) && !product.hasCategoryPriceList)
        .forEach((product) =>
            categories.set(product.category, { category: product.category, name: product.categoryName }),
        );
    return Array.from(categories.values());
};

/** The product's values today, or "not offered today" when the lab does not offer it. */
const ProductValues: FC<{ product: PriceRequestProduct; currency: string }> = ({ product, currency }) => {
    const { t: translate } = useTranslation('price-requests');
    const money = (value?: number | null) =>
        value === null || value === undefined ? '–' : formatCurrency(value, currency);
    return (
        <Typography variant="caption" color="text.secondary" display="block">
            {product.currentEnabled
                ? translate('PRODUCT_CURRENT_VALUES', {
                      price: money(product.current.price),
                      tiotandvardPrice: money(product.current.tiotandvardPrice),
                      deliveryTime: product.current.deliveryTime ?? '–',
                  })
                : translate('CURRENTLY_WITHDRAWN')}
        </Typography>
    );
};

/**
 * DD-3697: the creator's draft settings (EDIT_DRAFT) — the note and which products the request
 * covers. DD-3822: the choice is the backend's list of products the creator can include, so an
 * unticked product stays selectable, plus a lab ranking for each new category.
 */
const PriceRequestDraftEditor: FC<{ request: PriceRequest }> = ({ request }) => {
    const { t: translate } = useTranslation('price-requests');
    const [note, setNote] = useState(request.note ?? '');
    const [selected, setSelected] = useState(() => selectedOf(request));
    const [ranks, setRanks] = useState(() => ranksOf(request));
    const [feedback, setFeedback] = useState<{ severity: 'error' | 'success'; message: string }>();
    const products = request.availableProducts;

    useEffect(() => {
        setNote(request.note ?? '');
        setSelected(selectedOf(request));
        setRanks(ranksOf(request));
    }, [request]);

    const [updateDraft, { loading: isSaving }] = useMutation(updatePriceRequestDraftTemplate, {
        onCompleted: () => setFeedback({ severity: 'success', message: translate('SAVED') }),
        onError: (error) => setFeedback({ severity: 'error', message: translate(errorMessageKey(error)) }),
    });

    const rankedCategories = useMemo(() => unlistedCategoriesOf(products, selected), [products, selected]);

    const toggle = (action: string) =>
        setSelected((previous) => {
            const next = new Set(previous);
            if (next.has(action)) next.delete(action);
            else next.add(action);
            return next;
        });

    const save = () => {
        setFeedback(undefined);
        updateDraft({
            variables: {
                id: request._id,
                note: note.trim(),
                actionIds: products.filter((product) => selected.has(product.action)).map((product) => product.action),
                // An empty field is left out; the backend asks for it when the draft is sent.
                categoryRanks: rankedCategories
                    .filter(({ category }) => ranks[category])
                    .map(({ category }) => ({ category, rank: Number(ranks[category]) })),
            },
        });
    };

    return (
        <Accordion defaultExpanded sx={{ mb: 2 }} data-test="price-request.draft-editor">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{translate('DRAFT_SETTINGS')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={translate('NOTE')}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    inputProps={{ maxLength: MAX_NOTE_LENGTH, 'data-test': 'price-request.draft-note' }}
                />
                <Typography variant="body2" mt={2} mb={1}>
                    {translate('PRODUCTS_INCLUDED', { selected: selected.size, total: products.length })}
                </Typography>
                <FormGroup
                    sx={{
                        maxHeight: 280,
                        overflowY: 'auto',
                        ...alwaysVisibleScrollbar,
                        flexWrap: 'nowrap',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                    }}
                >
                    {products.map((product) => (
                        <FormControlLabel
                            key={product.action}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selected.has(product.action)}
                                    onChange={() => toggle(product.action)}
                                />
                            }
                            label={
                                <Box py={0.5}>
                                    <Typography variant="body2">
                                        {[product.actionNumber, product.actionName].filter(Boolean).join(' ') ||
                                            product.action}
                                    </Typography>
                                    <ProductValues product={product} currency={request.currency} />
                                </Box>
                            }
                        />
                    ))}
                </FormGroup>
                <PriceRequestCategoryRanks
                    categories={rankedCategories}
                    ranks={ranks}
                    onChange={(category, rank) => setRanks((previous) => ({ ...previous, [category]: rank }))}
                />
                {feedback ? (
                    <Alert severity={feedback.severity} sx={{ mt: 2 }} onClose={() => setFeedback(undefined)}>
                        {feedback.message}
                    </Alert>
                ) : undefined}
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button variant="contained" disabled={isSaving} onClick={save} data-test="price-request.draft-save">
                        {translate('SAVE_DRAFT_SETTINGS')}
                    </Button>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default PriceRequestDraftEditor;
